/**
 * Local persistence and URL bootstrap for staff-only CASL and tenant-feature
 * permission overrides (debug mode). Independent from Amplitude feature flags.
 */

import type { UserSession } from '@/api/client';
import {
  DEFAULT_ROLE_BASELINE,
  isDefaultRoleBaseline,
  serializeSessionRoleIndices,
  TEMPLATE_BASELINE_PREFIX,
} from '@/lib/auth/role-baseline-utils';
import { getDebugFromCookie } from '@/lib/debug/debug-state';

export { DEFAULT_ROLE_BASELINE };

export const PERMISSION_OVERRIDES_URL_PARAM = 'permissionOverrides';

/** Prefix for tenant-feature keys in share URLs (e.g. `tf:transitions:true`). */
export const TENANT_FEATURE_OVERRIDE_URL_PREFIX = 'tf:';

/** Prefix for role baseline entries in share URLs (e.g. `role:template:PropertyManager`). */
export const ROLE_BASELINE_URL_PREFIX = 'role:';

const STORAGE_KEYS = {
  OVERRIDES_ENABLED: 'permission-overrides-enabled',
  CASL_MAP: 'permission-overrides-casl-map',
  TENANT_FEATURES_MAP: 'permission-overrides-tenant-features-map',
  ROLE_BASELINE: 'permission-overrides-role-baseline',
} as const;

export type CaslOverrideMap = Record<string, boolean>;
export type TenantFeatureOverrideMap = Record<string, boolean>;

/** True when Surface UI debug is on and staff chose to apply stored override entries. */
export const shouldApplyPermissionOverrides = (): boolean =>
  getDebugFromCookie() && isPermissionOverridesEnabled();

/** True when debug + overrides-enabled + server session is admin. */
export const shouldApplyPermissionOverridesForSession = (
  session: UserSession
): boolean =>
  Boolean(session.is_admin) &&
  getDebugFromCookie() &&
  isPermissionOverridesEnabled();

export const isPermissionOverridesEnabled = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEYS.OVERRIDES_ENABLED) === 'true';
  } catch {
    return false;
  }
};

export const setPermissionOverridesEnabled = (enabled: boolean): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.OVERRIDES_ENABLED, enabled.toString());
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to set permission overrides enabled state:', error);
  }
};

export const getCaslOverrideMap = (): CaslOverrideMap => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.CASL_MAP);
    return stored ? (JSON.parse(stored) as CaslOverrideMap) : {};
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to read CASL permission overrides map:', error);
    return {};
  }
};

export const setCaslOverrideMap = (map: CaslOverrideMap): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.CASL_MAP, JSON.stringify(map));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to set CASL permission overrides map:', error);
  }
};

export const hasCaslOverride = (key: string): boolean =>
  Object.prototype.hasOwnProperty.call(getCaslOverrideMap(), key);

export const getTenantFeatureOverrideMap = (): TenantFeatureOverrideMap => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.TENANT_FEATURES_MAP);
    return stored ? (JSON.parse(stored) as TenantFeatureOverrideMap) : {};
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to read tenant-feature overrides map:', error);
    return {};
  }
};

export const setTenantFeatureOverrideMap = (
  map: TenantFeatureOverrideMap
): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.TENANT_FEATURES_MAP, JSON.stringify(map));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to set tenant-feature overrides map:', error);
  }
};

export const hasTenantFeatureOverride = (featureKey: string): boolean =>
  Object.prototype.hasOwnProperty.call(
    getTenantFeatureOverrideMap(),
    featureKey
  );

export const clearPermissionOverridesStorage = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.OVERRIDES_ENABLED);
    localStorage.removeItem(STORAGE_KEYS.CASL_MAP);
    localStorage.removeItem(STORAGE_KEYS.TENANT_FEATURES_MAP);
    localStorage.removeItem(STORAGE_KEYS.ROLE_BASELINE);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to clear permission overrides storage:', error);
  }
};

export const getRoleBaselineOverride = (): string => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.ROLE_BASELINE);
    return stored ?? DEFAULT_ROLE_BASELINE;
  } catch {
    return DEFAULT_ROLE_BASELINE;
  }
};

export const setRoleBaselineOverride = (baseline: string): void => {
  try {
    if (baseline === DEFAULT_ROLE_BASELINE) {
      localStorage.removeItem(STORAGE_KEYS.ROLE_BASELINE);
      return;
    }
    localStorage.setItem(STORAGE_KEYS.ROLE_BASELINE, baseline);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to set role baseline override:', error);
  }
};

/** Persists a subset of session role indices; all selected clears to default. */
export const setSessionRoleBaselineIndices = (
  indices: number[],
  roleCount: number
): void => {
  if (
    roleCount === 0 ||
    isDefaultRoleBaseline(serializeSessionRoleIndices(indices), roleCount)
  ) {
    setRoleBaselineOverride(DEFAULT_ROLE_BASELINE);
    return;
  }
  setRoleBaselineOverride(serializeSessionRoleIndices(indices));
};

export const setTemplateRoleBaseline = (templateName: string | null): void => {
  if (!templateName) {
    setRoleBaselineOverride(DEFAULT_ROLE_BASELINE);
    return;
  }
  setRoleBaselineOverride(`${TEMPLATE_BASELINE_PREFIX}${templateName}`);
};

export const hasNonDefaultRoleBaseline = (roleCount = 0): boolean => {
  const baseline = getRoleBaselineOverride();
  if (roleCount > 0) {
    return !isDefaultRoleBaseline(baseline, roleCount);
  }
  return baseline !== DEFAULT_ROLE_BASELINE;
};

export const getPermissionOverrideCount = (): number =>
  Object.keys(getCaslOverrideMap()).length +
  Object.keys(getTenantFeatureOverrideMap()).length;

function parseOverrideEntry(
  entry: string
):
  | { type: 'casl' | 'tenant'; key: string; value: boolean }
  | { type: 'role'; baseline: string }
  | null {
  const trimmed = entry.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith(ROLE_BASELINE_URL_PREFIX)) {
    const baseline = trimmed.slice(ROLE_BASELINE_URL_PREFIX.length);
    return baseline ? { type: 'role', baseline } : null;
  }

  if (trimmed.startsWith(TENANT_FEATURE_OVERRIDE_URL_PREFIX)) {
    const rest = trimmed.slice(TENANT_FEATURE_OVERRIDE_URL_PREFIX.length);
    const colonIdx = rest.lastIndexOf(':');
    if (colonIdx === -1) {
      return { type: 'tenant', key: rest, value: true };
    }
    return {
      type: 'tenant',
      key: rest.slice(0, colonIdx),
      value:
        rest
          .slice(colonIdx + 1)
          .trim()
          .toLowerCase() === 'true',
    };
  }

  const parts = trimmed.split(':');
  if (parts.length < 2) return null;

  const rawValue = parts.length >= 3 ? parts[parts.length - 1] : undefined;
  const action = parts.length >= 3 ? parts[parts.length - 2] : parts[1];
  const resource = parts.length >= 3 ? parts.slice(0, -2).join(':') : parts[0];
  const value =
    rawValue === undefined ? true : rawValue.trim().toLowerCase() === 'true';

  if (!resource || !action) return null;

  return {
    type: 'casl',
    key: `${resource}:${action}`,
    value,
  };
}

/**
 * Reads `?permissionOverrides=...` once: enables override application, merges into localStorage, strips param.
 * - `permissionOverrides=false` — only disables the overrides-enabled flag.
 * - `permissionOverrides=true` — enables with no parsed entries.
 * - `LeaseAudits:Read:false,tf:transitions:true` — CASL and tenant-feature entries.
 */
export const bootstrapPermissionOverridesFromUrl = (): void => {
  const params = new URL(window.location.href).searchParams;
  const raw = params.get(PERMISSION_OVERRIDES_URL_PARAM);
  if (raw === null) return;

  const removeURLParam = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete(PERMISSION_OVERRIDES_URL_PARAM);
    window.history.replaceState({}, document.title, url.toString());
  };

  if (raw === 'false') {
    setPermissionOverridesEnabled(false);
    removeURLParam();
    return;
  }

  if (!isPermissionOverridesEnabled()) {
    setPermissionOverridesEnabled(true);
  }

  if (raw !== 'true' && raw.length > 0) {
    const caslUpdates: CaslOverrideMap = {};
    const tenantUpdates: TenantFeatureOverrideMap = {};

    raw.split(',').forEach((entry) => {
      const parsed = parseOverrideEntry(entry);
      if (!parsed) return;
      if (parsed.type === 'role') {
        setRoleBaselineOverride(parsed.baseline);
        return;
      }
      if (parsed.type === 'casl') {
        caslUpdates[parsed.key] = parsed.value;
      } else {
        tenantUpdates[parsed.key] = parsed.value;
      }
    });

    if (Object.keys(caslUpdates).length > 0) {
      setCaslOverrideMap({ ...getCaslOverrideMap(), ...caslUpdates });
    }
    if (Object.keys(tenantUpdates).length > 0) {
      setTenantFeatureOverrideMap({
        ...getTenantFeatureOverrideMap(),
        ...tenantUpdates,
      });
    }
  }

  removeURLParam();
};

/** Builds the `permissionOverrides` query value when overrides are applying and non-empty. */
export const serializePermissionOverridesForShare = (
  session: UserSession
): string | null => {
  if (!shouldApplyPermissionOverridesForSession(session)) return null;

  const casl = getCaslOverrideMap();
  const tenant = getTenantFeatureOverrideMap();
  const roleBaseline = getRoleBaselineOverride();
  const parts = [
    ...(roleBaseline !== DEFAULT_ROLE_BASELINE
      ? [`${ROLE_BASELINE_URL_PREFIX}${roleBaseline}`]
      : []),
    ...Object.entries(casl).map(([k, v]) => `${k}:${v}`),
    ...Object.entries(tenant).map(
      ([k, v]) => `${TENANT_FEATURE_OVERRIDE_URL_PREFIX}${k}:${v}`
    ),
  ];

  return parts.length > 0 ? parts.join(',') : null;
};
